import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { CaretDown, CaretRight, MapPin } from "@phosphor-icons/react";
import { fmtDateTime } from "../../lib/dateUtil";

export const OUTCOME_META = {
  safe_to_proceed: { label: "Safe to proceed", cls: "bg-emerald-100 text-emerald-700" },
  proceed_with_controls: { label: "Extra controls", cls: "bg-amber-100 text-amber-800" },
  stop_work: { label: "STOP WORK", cls: "bg-red-600 text-white" },
};
const FILTERS = [["", "All"], ["stop_work", "Stop work"], ["proceed_with_controls", "Extra controls"], ["safe_to_proceed", "Safe"]];

/** Staff view of operatives' point-of-work dynamic risk assessments, newest first. */
export default function DraLogPanel({ entityType, entityId, embedded = false }) {
  const [rows, setRows] = useState([]);
  const [outcome, setOutcome] = useState("");
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    API.get("/hs/dra", { params: { outcome: outcome || undefined, entity_type: entityType, entity_id: entityId } })
      .then(({ data }) => setRows(data.assessments || [])).catch(() => {}).finally(() => setLoading(false));
  }, [outcome, entityType, entityId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="dra-log-panel">
      {!embedded && (
        <div className="flex gap-1 flex-wrap">
          {FILTERS.map(([k, l]) => <button key={k} onClick={() => setOutcome(k)} className={`text-xs px-2.5 py-1 rounded-full border ${outcome === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid={`dra-filter-${k || "all"}`}>{l}</button>)}
        </div>
      )}
      {loading && rows.length === 0 ? <p className="text-xs text-muted-foreground p-4">Loading…</p> : rows.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground" data-testid="dra-empty">No dynamic risk assessments yet. Operatives complete one on their job page before starting work.</div>
      ) : (
        <div className="border border-border rounded-lg divide-y divide-border">
          {rows.map(d => {
            const m = OUTCOME_META[d.outcome] || OUTCOME_META.safe_to_proceed;
            const isOpen = open === d.id;
            return (
              <div key={d.id} data-testid={`dra-row-${d.id}`}>
                <div className="flex items-center gap-2 px-3 py-2 hover:bg-muted/40 cursor-pointer" onClick={() => setOpen(isOpen ? null : d.id)}>
                  {isOpen ? <CaretDown size={14} className="text-muted-foreground" /> : <CaretRight size={14} className="text-muted-foreground" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate">{d.operative_name} · <span className="text-muted-foreground font-normal">{d.entity_reference || ""} {d.entity_title}</span></p>
                    <p className="text-[11px] text-muted-foreground">{fmtDateTime(d.created_at)}{d.risk_flags ? ` · ${d.risk_flags} flag${d.risk_flags === 1 ? "" : "s"}` : ""}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${m.cls}`} data-testid={`dra-outcome-${d.id}`}>{m.label}</span>
                </div>
                {isOpen && (
                  <div className="px-9 pb-3 text-xs space-y-2" data-testid={`dra-detail-${d.id}`}>
                    <ul className="grid md:grid-cols-2 gap-x-4 gap-y-0.5">
                      {(d.answers || []).map(a => (
                        <li key={a.key} className={`flex gap-1.5 ${a.answer === "no" ? "text-red-700 font-medium" : "text-muted-foreground"}`}>
                          <span className="w-7 shrink-0 uppercase font-bold">{a.answer === "na" ? "n/a" : a.answer}</span><span>{a.text}{a.note ? ` — ${a.note}` : ""}</span>
                        </li>
                      ))}
                    </ul>
                    {d.hazards_noted && <p><span className="font-semibold">Hazards noted:</span> {d.hazards_noted}</p>}
                    {d.controls && <p><span className="font-semibold">Extra controls:</span> {d.controls}</p>}
                    <div className="flex gap-3 text-[11px]">
                      {d.latitude && <a href={`https://www.google.com/maps?q=${d.latitude},${d.longitude}`} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1"><MapPin size={12} /> Location</a>}
                      {d.entity_id && <Link to={`/${d.entity_type === "emergency_job" ? "emergency-jobs" : "jobs"}/${d.entity_id}`} className="text-primary hover:underline">Open job</Link>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
